import { dynamicParams, customColumnParams } from './helpers'

// A View's AND/OR filter groups, ANDed with its flat filters by the API.
// A group is { op: 'and' | 'or', children }. A leaf is { filter, ui }: `filter` holds
// the API (snake_case) params the backend reads, `ui` the popover state the group
// builder edits. Only `filter` is sent with requests; saved Views keep both.

const isGroup = (node) => Array.isArray(node?.children)

const isBlank = (value) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

const VALUELESS_OPERATORS = ['is_empty', 'is_not_empty']

// A leaf's params that filter on something. An operator or mode left behind by a cleared value
// filters nothing, except is_empty / is_not_empty, which need no value.
export const activeFilterEntries = (filter = {}) => {
  const entries = Object.entries(filter).filter(([, value]) => !isBlank(value))
  const hasValue = entries.some(
    ([key, value]) =>
      !key.endsWith('_mode') && !(key.endsWith('_operator') && !VALUELESS_OPERATORS.includes(value))
  )
  return hasValue ? entries : []
}

export const emptyFilterTree = () => ({ op: 'and', children: [] })

// Saved Views store the tree as a JSON string; state holds the object.
export const parseFilterTree = (saved) => {
  if (!saved) return null
  if (typeof saved !== 'string') return saved
  try {
    return JSON.parse(saved)
  } catch {
    return null
  }
}

// The tree as the API reads it: empty leaves and groups dropped, editor state stripped.
// Null when nothing filters.
export const requestFilterTree = (node) => {
  if (!node) return null
  if (isGroup(node)) {
    const children = node.children.map(requestFilterTree).filter(Boolean)
    return children.length ? { op: node.op, children } : null
  }
  const filter = Object.fromEntries(activeFilterEntries(node.filter))
  return Object.keys(filter).length ? { filter } : null
}

export const filterTreeParam = (tree) => {
  const pruned = requestFilterTree(parseFilterTree(tree))
  return pruned ? { filter_tree: JSON.stringify(pruned) } : {}
}

// What a View saves: the whole tree, editor state included, or nothing once it is empty.
export const savedFilterTree = (tree) => (isGroup(tree) && tree.children.length ? JSON.stringify(tree) : undefined)

// API params for one leaf, from the same popover state a column header writes. Reuses the
// flat serializers so a leaf and a header filter on the same column send identical params.
export const leafFilter = (values, { columns = [], customColumns = [] } = {}) => ({
  ...dynamicParams(values, values, columns),
  ...customColumnParams(values, customColumns)
})
